import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import usePerformanceOptimization from '../../hooks/usePerformanceOptimization';
import useLazyAnimation from '../../hooks/useLazyAnimation';

const ParticleSystem = ({
  particleCount = 50,
  color = '#00D4FF',
  size = 2,
  speed = 1,
  className = '',
  interactive = false
}) => {
  const containerRef = useRef(null);
  const particles = useRef([]);
  const { shouldReduceAnimations } = usePerformanceOptimization();
  const { elementRef, shouldLoad } = useLazyAnimation({
    delay: 500,
    threshold: 0.2
  });

  // Reduce particle count on low-performance devices
  const optimizedParticleCount = shouldReduceAnimations ? Math.min(particleCount, 15) : particleCount;

  useEffect(() => {
    if (!containerRef.current || !shouldLoad) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();

    // Initialize particles only when they should load
    particles.current = Array.from({ length: optimizedParticleCount }, (_, i) => ({
      id: i,
      x: Math.random() * rect.width,
      y: Math.random() * rect.height,
      vx: (Math.random() - 0.5) * speed,
      vy: (Math.random() - 0.5) * speed,
      opacity: Math.random() * 0.8 + 0.2,
      scale: Math.random() * 0.8 + 0.2,
    }));
  }, [optimizedParticleCount, speed, shouldLoad]);

  const particleVariants = {
    animate: (custom) => ({
      x: [custom.x, custom.x + custom.vx * (shouldReduceAnimations ? 50 : 100), custom.x + custom.vx * (shouldReduceAnimations ? 100 : 200)],
      y: [custom.y, custom.y + custom.vy * (shouldReduceAnimations ? 50 : 100), custom.y + custom.vy * (shouldReduceAnimations ? 100 : 200)],
      opacity: [custom.opacity, custom.opacity * 0.5, custom.opacity],
      scale: [custom.scale, custom.scale * (shouldReduceAnimations ? 1.1 : 1.2), custom.scale],
      transition: {
        duration: shouldReduceAnimations ? 20 + Math.random() * 10 : 15 + Math.random() * 15,
        repeat: Infinity,
        ease: "linear",
      }
    })
  };

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        elementRef.current = node;
      }}
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
    >
      {shouldLoad && particles.current.map((particle) => (
        <motion.div
          key={particle.id}
          custom={particle}
          variants={shouldReduceAnimations ? {} : particleVariants}
          animate={shouldReduceAnimations ? false : "animate"}
          className="absolute rounded-full will-change-transform"
          style={{
            width: size,
            height: size,
            backgroundColor: color,
            boxShadow: shouldReduceAnimations ? 'none' : `0 0 ${size * 2}px ${color}`,
          }}
        />
      ))}
    </div>
  );
};

export default ParticleSystem;
